import {
    Avatar,
    Box,
    Flex,
    HStack,
    Heading,
    Skeleton,
    SkeletonCircle,
    Text,
    VStack,
    useMediaQuery,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Button,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { SubmitDetails } from "src/types/userDetails";
import { useNavigate, useLocation } from "react-router-dom";

import ContactIcon from "src/resources/icons/ContactIcon";
import DoneIcon from "src/resources/icons/DoneIcon";
import { EnglishLabels } from "src/common/constants/englishLabels";
import { GlobalUtils } from "src/utils/globalUtils";
import HeaderComponent from "../register/HeaderComponent";
import { HindiLabels } from "src/common/constants/hindiLabels";
import HourGlassIcon from "src/resources/icons/HourGlassIcon";
import { MksLogo } from "src/resources/icons";
import { RootState } from "src/store/store";
import { Tag } from "src/common/formConstants";
import { useSelector } from "react-redux";


// Normalize name for consistent display and avatar initials
const sanitizeName = (name: string): string => {
    const n = String(name || "").trim().replace(/\s+/g, " ");
    if (!n) return "";
    const parts = n.split(" ");
    if (parts.length === 1) return parts[0];
    // If two words are identical (case-insensitive), collapse to one
    if (parts.length === 2 && parts[0].toLowerCase() === parts[1].toLowerCase()) {
        return parts[0];
    }
    return n;
};


const SubmitPage = () => {
    const globalInfo = useSelector((state: RootState) => state.globalInfo);
    const { globalLanguage } = globalInfo;
    const isHindiLabels = globalLanguage === "HI";

    const [isMobile] = useMediaQuery("(max-width: 426px)");
    const [isTablet] = useMediaQuery("(min-width: 426px) and (max-width: 768px)");

    const [profile, setProfile] = useState<SubmitDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);


    const token = window.localStorage.getItem("authToken") || "";
    const isApproved = GlobalUtils.getAuthStateFromToken(token).approved;
    const application_status = GlobalUtils.getAuthStateFromToken(token).applicationStatus;

    const navigate = useNavigate();
    const location = useLocation();
    const [showConfirm, setShowConfirm] = useState(false);
    const cancelRef = useRef<HTMLButtonElement | null>(null);

    // If user is approved, redirect away from Submit
    useEffect(() => {
        if (isApproved) {
            navigate("/profile", { replace: true });
        }
    }, [isApproved, navigate]);

    // Intercept navigation attempts for unapproved users
    useEffect(() => {
        if (isApproved) return; // Only for pending users

        const clickHandler = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Find nearest anchor
            const anchor = target.closest("a") as HTMLAnchorElement | null;
            if (!anchor) return;
            const href = anchor.getAttribute("href") || "";
            // Allow staying on submit
            const isSubmitLink = href === "/submit" || href === location.pathname;
            if (!isSubmitLink) {
                e.preventDefault();
                setShowConfirm(true);
            }
        };

        const popHandler = () => {
            // User is trying to use browser back/forward
            // If resulting path is not /submit, show confirm and push back to submit
            const next = window.location.pathname;
            if (next !== "/submit") {
                setShowConfirm(true);
                // push back to submit to avoid route change until confirmed
                navigate("/submit", { replace: true });
            }
        };

        document.addEventListener("click", clickHandler, true);
        window.addEventListener("popstate", popHandler);
        return () => {
            document.removeEventListener("click", clickHandler, true);
            window.removeEventListener("popstate", popHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isApproved, location.pathname]);

    const handleConfirmContinue = () => {
        // Logout and redirect to Home
        try {
            window.localStorage.removeItem("authToken");
            window.localStorage.removeItem("userRole");
            window.localStorage.removeItem("accountInfo");
            window.localStorage.removeItem("cachedSubmitDetails");
            window.localStorage.removeItem("pendingAccess");
            window.localStorage.removeItem("approved");
        } catch {}
        setShowConfirm(false);
        navigate("/", { replace: true });
    };

    const handleConfirmCancel = () => {
        // Stay on Submit
        setShowConfirm(false);
        navigate("/submit", { replace: true });
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                setError(null);

                // Unapproved users: render without token
                if (!isApproved) {
                    const cachedRaw = window.localStorage.getItem("cachedSubmitDetails");
                    if (cachedRaw) {
                        const parsed = JSON.parse(cachedRaw);
                        const normalized = { ...parsed, fullName: sanitizeName(parsed.fullName) };
                        setProfile(normalized);
                        return;
                    }
                    const account = GlobalUtils.getUserLoginInfo();
                    setProfile({
                        fullName: sanitizeName(account?.emailId?.split("@")[0] || ""),
                        dob: "",
                        contactNo: account?.mobileNo || "",
                        educationalQualification: "",
                        occupation: "",
                        businessDetails: "",
                        applicationStatus: "Submitted"
                    });
                    return;
                }
            } catch (err: any) {
                console.error("Error fetching profile:", err);
                setError(err.message || "An unknown error occurred");
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isApproved]);

    // Only show error for approved path
    if (error && isApproved) {
        return (
            <Flex justify="center" align="center" height="100vh">
                <Text color="red.500">Error: {error}</Text>
            </Flex>
        );
    }

    return (
        <Box height="100%" px="2rem">
            {/* Confirmation Dialog for pending users */}
            <AlertDialog isOpen={showConfirm} leastDestructiveRef={cancelRef as any} onClose={() => setShowConfirm(false)}>
                <AlertDialogOverlay>
                    <AlertDialogContent maxW="600px" p={6}>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Notice
                        </AlertDialogHeader>
                        <AlertDialogBody fontSize="lg">
                            You are not yet approved by the admin. If you proceed to any other page, you will be logged out.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef as any} onClick={handleConfirmCancel} variant="outline">
                                Cancel
                            </Button>
                            <Button bg="#4A2C2A" color="white" _hover={{ bg: "#4A2C2A" }} onClick={handleConfirmContinue} ml={3}>
                                Continue
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <HStack width="100%" mb="2rem">
                <HeaderComponent />
            </HStack>

            <Flex mt={2} justifyContent="center" width="100%">
                <VStack width={isMobile || isTablet ? "100%" : "50%"} spacing={4}>
                    {/* Header Box */}
                    <Box width="100%" height="7rem" borderRadius="20px" background="#FFF0E0">
                        <Flex direction={isMobile || isTablet ? "column" : "row"} justifyContent="space-between">
                            <Flex alignItems="center">
                                <Box borderRadius="10px" p={1} m={2} bg="white">
                                    {loading ? <SkeletonCircle size="12" /> : <MksLogo height="3rem" width="3rem" />}
                                </Box>
                                <Box ml={4}>
                                    <Heading
                                        lineHeight="100%"
                                        width="12.8rem"
                                        color="#4A2C2A"
                                        fontWeight="500"
                                        fontSize="1.2rem"
                                        letterSpacing="-0.32px"
                                    >
                                        {loading
                                            ? <Skeleton height="24px" width="200px" />
                                            : isHindiLabels
                                                ? HindiLabels.Mourya_Community_Label
                                                : EnglishLabels.Mourya_Community_Label}
                                    </Heading>
                                </Box>
                            </Flex>
                        </Flex>
                    </Box>

                    {/* Profile Picture */}
                    <Box bottom={12} position="relative">
                        {loading ? (
                            <SkeletonCircle size="16" />
                        ) : (
                            <Avatar
                                boxShadow="0px 14px 38px rgba(0, 0, 0, 0.1)"
                                border="1px solid #4A2C2A"
                                size="lg"
                                name={profile!.fullName}
                                src={"https://bit.ly/code-beast"}
                            />
                        )}
                    </Box>

                    {/* Name */}
                    <HStack justifyContent="center">
                        {loading ? (
                            <Skeleton height="28px" width="160px" mt={-16} />
                        ) : (
                            <Heading mt={-16} variant="h4" color="#2B2D42">
                                {profile!.fullName}
                            </Heading>
                        )}
                    </HStack>

                    {/* Contact Info */}
                    <HStack
                        p={2}
                        boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)"
                        borderRadius="16px"
                        border="1px solid rgba(108, 117, 125, 0.18)"
                        px={8}
                        justifyContent="space-between"
                    >
                        <HStack>
                            <ContactIcon />
                            <VStack align="start">
                                <Text textStyle="featureBold" color="#1F0812">
                                    Contact No.
                                </Text>
                                {loading ? (
                                    <Skeleton height="20px" width="120px" />
                                ) : (
                                    <Text textStyle="highlightAccent" color="#6C757D">
                                        {profile!.contactNo || "N/A"}
                                    </Text>
                                )}
                            </VStack>
                        </HStack>

                        <VStack align="start">
                            <Text textStyle="featureBold" color="#1F0812">
                                D.O.B
                            </Text>
                            {loading ? (
                                <Skeleton height="20px" width="80px" />
                            ) : (
                                <Text textStyle="highlightAccent" color="#6C757D">
                                    {profile!.dob || "N/A"}
                                </Text>
                            )}
                        </VStack>
                    </HStack>

                    {/* Education, Occupation, Business */}
                    <HStack
                        p={6}
                        alignItems="start"
                        width="100%"
                        boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)"
                        background="#FFF"
                        borderRadius="16px"
                        border="1px solid rgba(108, 117, 125, 0.18)"
                        mt={4}
                    >
                        <VStack width="100%" spacing={4} align="start">
                            {["Educational Qualification", "Occupation", "Business Details"].map((label, idx) => (
                                <HStack justifyContent="space-between" width="100%" key={idx}>
                                    <Text fontWeight="bold" color="#6C757D">{label}:</Text>
                                    {loading ? (
                                        <Skeleton height="20px" width="120px" />
                                    ) : (
                                        <Text>
                                            {idx === 0
                                                ? profile!.educationalQualification || "N/A"
                                                : idx === 1
                                                    ? profile!.occupation || "N/A"
                                                    : profile!.businessDetails || "N/A"}
                                        </Text>
                                    )}
                                </HStack>
                            ))}
                        </VStack>
                    </HStack>

                    {/* Application Details */}
                    <HStack
                        p={6}
                        alignItems="start"
                        width="100%"
                        boxShadow="0px 17px 38px rgba(120, 120, 120, 0.1)"
                        background="#FFF"
                        borderRadius="16px"
                        border="1px solid rgba(108, 117, 125, 0.18)"
                    >
                        <VStack width="100%">
                            {["Application submission", "Background verification"].map((title, idx) => (
                                <VStack p={3} width="100%" key={idx}>
                                    <HStack width="100%" justifyContent="space-between">
                                        <Heading letterSpacing="-0.48px" color="#6C757D" variant="h6">
                                            {title}
                                        </Heading>
                                        {loading ? (
                                            <Skeleton height="20px" width="80px" />
                                        ) : (
                                            <Text>
                                                {idx === 0
                                                    ? (application_status || profile!.applicationStatus) === "In_Progress"
                                                        ? "In Progress"
                                                        : (application_status || profile!.applicationStatus)
                                                    : "Pending"}
                                            </Text>
                                        )}
                                    </HStack>
                                    <Flex width="100%" justifyContent="flex-start">
                                        {loading ? (
                                            <Skeleton height="30px" width="120px" borderRadius="8px" />
                                        ) : (
                                            <Tag
                                                leftIcon={idx === 0 ? DoneIcon : HourGlassIcon}
                                                label={idx === 0 ? "Submitted" : "Pending"}
                                                backgroundColor={
                                                    idx === 0
                                                        ? "rgba(52, 168, 83, 0.08)"
                                                        : "rgba(255, 202, 150, 0.29)"
                                                }
                                            />
                                        )}
                                    </Flex>
                                </VStack>
                            ))}
                        </VStack>
                    </HStack>
                </VStack>
            </Flex>
        </Box>
    );
};

export default SubmitPage;
